import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import FileInput from '../../../components/FileInput/FileInput';
import { iconList } from 'assets';
import { DownloadIcon, File, RemoveIcon, ToUploadList } from '../ChangeRequest.styled';

const EditChangeRequest = ({
  state,
  dispatch,
  updateFieldCreator,
  errors,
  updateChangeRequest,
  removeError,
  profile,
  type,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  useEffect(() => {
    if (profile && profile.id) {
      dispatch(updateFieldCreator({ name: 'description', value: profile.description || '' }));
      if (profile.files) {
        setExistingFiles(profile.files);
      }
    }
  }, [profile, dispatch, updateFieldCreator]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setSelectedFiles([...selectedFiles, ...newFiles]);
    if (errors.files) removeError(clearErrors(errors, 'files'));
    // Reset the input to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (fileId) => {
    setExistingFiles(existingFiles.filter((file) => file.id !== fileId));
  };

  const submit = () => {
    const formData = new FormData();
    formData.append('description', state.description || '');
    
    selectedFiles.forEach((file) => {
      formData.append('files[]', file);
    });

    // Add files to remove
    const filesToRemove = profile.files
      ?.filter((file) => !existingFiles.find((ef) => ef.id === file.id))
      .map((file) => file.id) || [];
    
    if (filesToRemove.length > 0) {
      formData.append('filesToRemove', JSON.stringify(filesToRemove));
    }

    updateChangeRequest(profile.id, formData, history);
  };

  const isApproved = profile?.status === 'Approved';
  const isImplemented = profile?.status === 'Implemented';
  const isAbandoned = profile?.status === 'Abandoned';
  const canEdit = !isApproved && !isImplemented && !isAbandoned;

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">
              {t('page.user.cis.changeRequests.editChangeRequest')}
            </Heading>
            {(isApproved || isImplemented || isAbandoned) && (
              <p style={{ color: 'orange', marginTop: '10px' }}>
                {t('page.user.cis.changeRequests.cannotEditFinalStatus')}
              </p>
            )}
          </Margin>
          <Row>
            <Col size={{ xl: 1 }}>
              <Margin bottom="20">
                <Field
                  id="description"
                  label={t('page.user.cis.changeRequests.description')}
                  error={errors.description}
                >
                  <textarea
                    id="description"
                    rows="5"
                    placeholder={t('page.user.cis.changeRequests.descriptionPlaceholder')}
                    value={state.description}
                    onChange={handleChangeNative('description', 'description')}
                    disabled={!canEdit}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          {existingFiles.length > 0 && (
            <Row>
              <Col size={{ xl: 1 }}>
                <Margin bottom="20">
                  <Field
                    id="existingFiles"
                    label={t('page.user.cis.changeRequests.existingFiles')}
                  >
                    <ToUploadList>
                      {existingFiles.map((file) => (
                        <File key={file.id}>
                          {file.fileUrl && (
                            <DownloadIcon
                              src={iconList.fileUpload}
                              title="Download file"
                              onClick={() => window.open(file.fileUrl, '_blank')}
                            />
                          )}
                          <RemoveIcon
                            src={iconList.close}
                            title="Remove file"
                            onClick={() => canEdit && handleRemoveExistingFile(file.id)}
                            style={{ cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.5 }}
                          />
                          {file.fileName || file.name || file.originalName || `File ${file.id}`}
                        </File>
                      ))}
                    </ToUploadList>
                  </Field>
                </Margin>
              </Col>
            </Row>
          )}
          {canEdit && (
            <Row>
              <Col size={{ xl: 1 / 3}}>
                <Margin bottom="20">
                  <Field
                    id="files"
                    label={t('page.user.cis.changeRequests.addFiles')}
                    error={errors.files}
                  >
                    <FileInput
                      multiple
                      placeholder={t('page.user.cis.changeRequests.addFiles')}
                      onChange={handleFileChange}
                      clear={() => {}}
                      fileName=""
                    />
                  </Field>
                  {selectedFiles.length > 0 && (
                    <ToUploadList>
                      {selectedFiles.map((file, index) => (
                        <File key={`${file.name}-${index}`}>
                          <RemoveIcon
                            src={iconList.close}
                            title="Remove file"
                            onClick={() => handleRemoveSelectedFile(index)}
                          />
                          {file.name}
                        </File>
                      ))}
                    </ToUploadList>
                  )}
                </Margin>
              </Col>
            </Row>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
                <Button onClick={submit} type="button" disabled={!canEdit}>
                  {t('global.submit')}
                </Button>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
                <Button secondary onClick={() => history.goBack()} type="button" style={{ marginLeft: '10px' }}>
                  {t('global.cancel')}
                </Button>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditChangeRequest.propTypes = {
  state: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    files: PropTypes.array,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    description: PropTypes.string,
    files: PropTypes.string,
  }),
  updateChangeRequest: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  profile: PropTypes.object,
  type: PropTypes.string,
};

EditChangeRequest.defaultProps = {
  errors: {},
  profile: {},
  type: 'ma',
};

export default EditChangeRequest;
